<template>
	<div class="user-settings">
		<section class="card">
			<h3>个人资料设置</h3>
			<el-form ref="profileFormRef" :model="profileForm" :rules="profileRules" label-width="120px">
				<el-form-item label="昵称" prop="nickname">
					<el-input v-model.trim="profileForm.nickname" maxlength="30" />
				</el-form-item>
				<el-form-item label="邮箱" prop="email">
					<el-input v-model.trim="profileForm.email" />
				</el-form-item>
				<el-form-item label="头像">
					<div class="avatar-row">
						<img class="avatar-preview" :src="profileForm.avatar || '/img/avatar.jpg'" alt="avatar">
						<div class="avatar-tools">
							<el-upload
								:auto-upload="false"
								:show-file-list="false"
								:on-change="onAvatarChange"
								accept="image/*">
								<el-button size="small">选择图片</el-button>
							</el-upload>
							<el-input v-model.trim="profileForm.avatar" placeholder="或输入头像 URL" />
						</div>
					</div>
				</el-form-item>
				<el-form-item label="简介">
					<el-input v-model.trim="profileForm.bio" type="textarea" :rows="3" maxlength="160" show-word-limit />
				</el-form-item>
				<el-form-item>
					<el-button type="primary" :loading="savingProfile" @click="saveProfile">保存资料</el-button>
				</el-form-item>
			</el-form>
		</section>

		<section class="card">
			<h3>账号安全设置</h3>
			<el-form ref="securityFormRef" :model="securityForm" :rules="securityRules" label-width="140px">
				<el-form-item label="当前密码" prop="oldPassword">
					<el-input v-model="securityForm.oldPassword" type="password" show-password />
				</el-form-item>
				<el-form-item label="新密码" prop="newPassword">
					<el-input v-model="securityForm.newPassword" type="password" show-password />
				</el-form-item>
				<el-form-item label="确认新密码" prop="confirmPassword">
					<el-input v-model="securityForm.confirmPassword" type="password" show-password />
				</el-form-item>
				<el-form-item>
					<el-button type="warning" :loading="savingSecurity" @click="saveSecurity">更新密码</el-button>
				</el-form-item>
			</el-form>
		</section>

		<section class="card">
			<h3>偏好设置</h3>
			<el-form label-width="140px">
				<el-form-item label="主题模式">
					<el-radio-group v-model="preferences.themeMode">
						<el-radio label="light">浅色</el-radio>
						<el-radio label="dark">深色</el-radio>
					</el-radio-group>
				</el-form-item>
				<el-form-item label="显示时间轴">
					<el-switch v-model="preferences.showTimeline" />
				</el-form-item>
				<el-form-item label="紧凑动态卡片">
					<el-switch v-model="preferences.compactMoments" />
				</el-form-item>
				<el-form-item>
					<el-button type="success" :loading="savingPreferences" @click="savePreferences">保存偏好</el-button>
				</el-form-item>
			</el-form>
		</section>
	</div>
</template>

<script>
import {uploadUserAvatar, updateAccountPassword} from '@/api/user'
import {getViewUser, saveViewUserProfile, saveViewPreferences, getViewPreferences} from '@/util/viewAuth'

export default {
	name: 'UserSettings',
	data() {
		const confirmPasswordValidator = (rule, value, callback) => {
			if (!value) {
				callback(new Error('请再次输入新密码'))
				return
			}
			if (value !== this.securityForm.newPassword) {
				callback(new Error('两次输入的密码不一致'))
				return
			}
			callback()
		}
		return {
			profileForm: {
				username: '',
				nickname: '',
				email: '',
				avatar: '',
				bio: ''
			},
			securityForm: {
				oldPassword: '',
				newPassword: '',
				confirmPassword: ''
			},
			preferences: {
				themeMode: 'light',
				showTimeline: true,
				compactMoments: false
			},
			avatarFile: null,
			savingProfile: false,
			savingSecurity: false,
			savingPreferences: false,
			profileRules: {
				nickname: [{required: true, message: '请输入昵称', trigger: 'blur'}],
				email: [
					{type: 'email', message: '邮箱格式不正确', trigger: 'blur'}
				]
			},
			securityRules: {
				oldPassword: [{required: true, message: '请输入当前密码', trigger: 'blur'}],
				newPassword: [
					{required: true, message: '请输入新密码', trigger: 'blur'},
					{min: 6, message: '密码长度至少 6 位', trigger: 'blur'}
				],
				confirmPassword: [{validator: confirmPasswordValidator, trigger: 'blur'}]
			}
		}
	},
	created() {
		this.initForms()
	},
	methods: {
		initForms() {
			const user = getViewUser()
			this.profileForm = {
				username: user.username || '',
				nickname: user.nickname || '',
				email: user.email || '',
				avatar: user.avatar || '/img/avatar.jpg',
				bio: user.bio || ''
			}
			this.preferences = getViewPreferences()
		},
		onAvatarChange(file) {
			this.avatarFile = file.raw
			this.profileForm.avatar = URL.createObjectURL(file.raw)
		},
		saveProfile() {
			this.$refs.profileFormRef.validate(valid => {
				if (!valid) {
					return
				}
				this.savingProfile = true
				const uploadTask = this.avatarFile ? uploadUserAvatar(this.avatarFile) : Promise.resolve(null)
				uploadTask.then(res => {
					const avatarUrl = res && res.data ? res.data : this.profileForm.avatar
					saveViewUserProfile({
						nickname: this.profileForm.nickname,
						email: this.profileForm.email,
						avatar: avatarUrl,
						bio: this.profileForm.bio
					})
					this.profileForm.avatar = avatarUrl
					this.avatarFile = null
					this.msgSuccess('资料保存成功')
				}).catch(() => {
					this.msgError('资料保存失败')
				}).finally(() => {
					this.savingProfile = false
				})
			})
		},
		saveSecurity() {
			this.$refs.securityFormRef.validate(valid => {
				if (!valid) {
					return
				}
				this.savingSecurity = true
				updateAccountPassword({
					username: this.profileForm.username,
					password: this.securityForm.newPassword
				}).then(res => {
					this.msgSuccess(res.msg || '密码更新成功')
					this.securityForm.oldPassword = ''
					this.securityForm.newPassword = ''
					this.securityForm.confirmPassword = ''
				}).catch(() => {
					this.msgError('密码更新失败')
				}).finally(() => {
					this.savingSecurity = false
				})
			})
		},
		savePreferences() {
			this.savingPreferences = true
			try {
				saveViewPreferences(this.preferences)
				this.msgSuccess('偏好设置已保存')
			} finally {
				this.savingPreferences = false
			}
		}
	}
}
</script>

<style scoped>
.user-settings {
	display: grid;
	gap: 12px;
}

.card {
	background: #fff;
	border: 1px solid #e7eef5;
	border-radius: 14px;
	padding: 14px;
}

.card h3 {
	margin: 0 0 12px;
	font-size: 16px;
	color: #0f172a;
}

.avatar-row {
	display: flex;
	gap: 12px;
	align-items: center;
}

.avatar-preview {
	width: 62px;
	height: 62px;
	border-radius: 50%;
	object-fit: cover;
	border: 2px solid #dbeafe;
}

.avatar-tools {
	display: flex;
	flex-direction: column;
	gap: 8px;
	flex: 1;
}

@media (max-width: 768px) {
	.avatar-row {
		flex-direction: column;
		align-items: flex-start;
	}
}
</style>
